import React, { useEffect, useRef, useState } from "react";
import { Card, Spinner, Form, Row, Col } from "@themesberg/react-bootstrap";
import { Button, ButtonGroup } from "@themesberg/react-bootstrap";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileDownload } from "@fortawesome/free-solid-svg-icons";
import { fetchApi } from "../../../redux/api";
import { formatNumber } from "../../../redux/utils";
import moment from "moment";

export default function ReportCapitalProject({
  type = "capital_exp_by_project",
  route = "/reports",
  options = [],
  title = "Capital Expenses by Project",
}) {
  const [treeData, setTreeData] = useState([]);
  const [form, setForm] = useState({
    budget_year: moment().format("YYYY"),
  });
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState({
    value: type,
    title,
  });
  const years = Array.from({ length: 10 }, (_, i) =>
    moment().format("YYYY") - i
  );
  const tableRef = useRef(null);

  const handleChange = ({ target: { name, value } }) => {
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleButtonClick = (value, title) => {
    setSelectedOption({ value, title });
  };

  useEffect(() => {
    setLoading(true);
    fetchApi(
      `reports?query_type=${selectedOption.value}&budget_year=${form.budget_year}`
    )
      .then((data) => {
        setTreeData(data?.results || []);
      })
      .catch((err) => console.log(err))
      .finally(() => setLoading(false));
  }, [form.budget_year, selectedOption.value]);

  const getTotal = (value) => {
    return treeData?.reduce(
      (total, item) => total + parseFloat(item[value] || 0),
      0
    );
  };

  const styles = {
    textAlign: "right",
    backgroundColor: "#eee",
    fontWeight: "bold",
  };

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <h4>{selectedOption.title}</h4>
        </div>
      </div>

      <Card border="light" className="shadow-sm">
        <Card.Body>
          <Row className="mb-3">
            <Col md={2}>
              <Form.Group>
                <Form.Label>Budget Year</Form.Label>
                <Form.Select
                  name="budget_year"
                  value={form.budget_year}
                  onChange={handleChange}
                >
                  <option>--select--</option>
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={8} className="text-center">
              <ButtonGroup>
                <Button
                  size="sm"
                  variant={
                    selectedOption.title === title
                      ? "primary"
                      : "outline-secondary"
                  }
                  onClick={() => handleButtonClick(type, title)}
                >
                  <small>{title}</small>
                </Button>
                {options.map((item) => (
                  <Button
                    size="sm"
                    key={item.title}
                    variant={
                      selectedOption.title === item.title
                        ? "primary"
                        : "outline-secondary"
                    }
                    onClick={() =>
                      handleButtonClick(item.value, item.title)
                    }
                  >
                    <small>{item.title}</small>
                  </Button>
                ))}
              </ButtonGroup>
            </Col>
            <Col
              md={2}
              className="d-flex align-items-end justify-content-end"
            >
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => {
                  if (tableRef.current) {
                    const ws = XLSX.utils.table_to_sheet(tableRef.current);
                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(
                      wb,
                      ws,
                      selectedOption.title
                    );
                    const buf = XLSX.write(wb, {
                      bookType: "xlsx",
                      type: "array",
                    });
                    FileSaver.saveAs(
                      new Blob([buf], {
                        type: "application/octet-stream",
                      }),
                      `${selectedOption.title}.xlsx`
                    );
                  }
                }}
              >
                <FontAwesomeIcon icon={faFileDownload} className="me-1" />{" "}
                Download Excel
              </Button>
            </Col>
          </Row>

          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <div className="table-responsive">
              <table
                className="table table-bordered"
                ref={tableRef}
                style={{ fontSize: "13px" }}
              >
                <thead>
                  <tr
                    style={{
                      fontSize: "12px",
                      textAlign: "center",
                      backgroundColor: "orange",
                    }}
                  >
                    <th style={{ backgroundColor: "orange" }}>Project Name</th>
                    <th style={{ backgroundColor: "orange" }}>
                      Programme Code & Desc
                    </th>
                    <th style={{ backgroundColor: "orange" }}>
                      Administrative Code & Desc
                    </th>
                    <th style={{ backgroundColor: "orange" }}>
                      Economic Code & Desc
                    </th>
                    <th style={{ backgroundColor: "orange" }}>
                      Function Code & Desc
                    </th>
                    <th style={{ backgroundColor: "orange" }}>
                      Location Code & Desc
                    </th>
                    <th style={{ backgroundColor: "orange" }}>{`${
                      parseInt(form.budget_year) - 2
                    } Full Year Actual`}</th>
                    <th style={{ backgroundColor: "orange" }}>{`${
                      parseInt(form.budget_year) - 1
                    } Approved Budget`}</th>
                    <th style={{ backgroundColor: "orange" }}>{`${
                      parseInt(form.budget_year) - 1
                    } Performance Jan-Aug`}</th>
                    <th style={{ backgroundColor: "orange" }}>{`${parseInt(
                      form.budget_year
                    )} MDA Proposal`}</th>
                    <th style={{ backgroundColor: "orange" }}>{`${parseInt(
                      form.budget_year
                    )} Adjustments`}</th>
                    <th style={{ backgroundColor: "orange" }}>{`${parseInt(
                      form.budget_year
                    )} Approved Budget`}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ fontSize: "12px", backgroundColor: "#eee" }}>
                    <td style={{ ...styles, textAlign: "left" }}>
                      Total Capital Expenditure
                    </td>
                    <td style={styles}></td>
                    <td style={styles}></td>
                    <td style={styles}></td>
                    <td style={styles}></td>
                    <td style={styles}></td>
                    <td style={styles}>
                      {formatNumber(getTotal("prev_year2_actual_amount"))}
                    </td>
                    <td style={styles}>
                      {formatNumber(getTotal("prev_year1_approve_amount"))}
                    </td>
                    <td style={styles}>
                      {formatNumber(getTotal("prev_year1_actual_amount"))}
                    </td>
                    <td style={styles}>
                      {formatNumber(getTotal("budget_amount"))}
                    </td>
                    <td style={styles}>
                      {formatNumber(getTotal("supplementary_amount"))}
                    </td>
                    <td style={styles}>
                      {formatNumber(getTotal("approve_amount"))}
                    </td>
                  </tr>
                  {treeData.length === 0 ? (
                    <tr>
                      <td colSpan={12} className="text-center py-4">
                        No data available
                      </td>
                    </tr>
                  ) : (
                    treeData.map((item, index) => (
                      <tr
                        key={`row_${index}`}
                        style={{ fontSize: "12px" }}
                      >
                        <td>{item.description}</td>
                        <td>
                          {item.program_code}-{item.program_description}
                        </td>
                        <td>
                          {item.admin_code}-{item.admin_description}
                        </td>
                        <td>
                          {item.economic_code}-{item.economic_description}
                        </td>
                        <td>
                          {item.function_code}-{item.function_description}
                        </td>
                        <td>
                          {item.geo_code}-{item.geo_description}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {isNaN(item.prev_year2_actual_amount)
                            ? "---"
                            : formatNumber(item.prev_year2_actual_amount)}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {isNaN(item.prev_year1_approve_amount)
                            ? "---"
                            : formatNumber(item.prev_year1_approve_amount)}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {isNaN(item.prev_year1_actual_amount)
                            ? "---"
                            : formatNumber(item.prev_year1_actual_amount)}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {isNaN(item.budget_amount)
                            ? "---"
                            : formatNumber(item.budget_amount)}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {isNaN(item.supplementary_amount)
                            ? "---"
                            : formatNumber(item.supplementary_amount)}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {isNaN(item.approve_amount)
                            ? "---"
                            : formatNumber(item.approve_amount)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>
    </>
  );
}
